import { Injectable } from '@angular/core';

/** Number of trailing phone digits kept visible when masking. */
const PHONE_VISIBLE_DIGITS = 4;
/** Number of asterisks used to mask the area-code segment. */
const AREA_CODE_LENGTH = 3;
/** Number of asterisks used to mask the exchange segment. */
const EXCHANGE_LENGTH = 3;

@Injectable({
  providedIn: 'root',
})
export class MaskingService {
  /**
   * Masks a phone number by showing only the last 4 digits
   * @param phoneNumber The phone number to mask
   * @returns Masked phone number in format (***) ***-1234
   */
  maskPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';

    // Remove any formatting to get just digits
    const digits = phoneNumber.replace(/\D/g, '');

    // If less than 10 digits, just mask everything except last 4
    if (digits.length < 10) {
      const visible = digits.slice(-PHONE_VISIBLE_DIGITS);
      const masked = '*'.repeat(Math.max(0, digits.length - PHONE_VISIBLE_DIGITS));
      return masked + visible;
    }

    // For 10+ digit numbers, show format like (***) ***-1234
    const lastFour = digits.slice(-PHONE_VISIBLE_DIGITS);

    return this.formatMaskedPhone(lastFour);
  }

  /**
   * Builds the masked-phone display string for a 10+ digit number,
   * e.g. (***) ***-1234
   * @param lastFour The last four (visible) digits of the phone number
   */
  private formatMaskedPhone(lastFour: string): string {
    const areaCode = '*'.repeat(AREA_CODE_LENGTH);
    const exchange = '*'.repeat(EXCHANGE_LENGTH);

    return `(${areaCode}) ${exchange}-${lastFour}`;
  }

  /**
   * Masks an email address by showing first 2 and last 2 characters of local part
   * @param email The email address to mask
   * @returns Masked email address like bo****er@example.com
   */
  maskEmail(email: string): string {
    if (!email) return '';

    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;

    // For local part, show first 2 characters and last 2 characters
    let maskedLocal: string;
    if (localPart.length <= 4) {
      // If local part is 4 or fewer characters, mask all but the last character
      maskedLocal = '*'.repeat(Math.max(0, localPart.length - 1)) + localPart.slice(-1);
    } else {
      // Show first 2 and last 2 characters
      const start = localPart.slice(0, 2);
      const end = localPart.slice(-2);
      const middleMask = '*'.repeat(localPart.length - 4);
      maskedLocal = start + middleMask + end;
    }

    return `${maskedLocal}@${domain}`;
  }
}
